<template>
  <div
    class="model-edit-form-price-agreement space-y-2"
    :class="{ 'rounded-md border p-2': framed }"
  >
    <div v-if="framed" class="flex items-center space-x-2">
      <span v-if="entityName" class="text-sm font-normal">{{ entityName }}</span>
      <PProgress
        as="span"
        size="sm"
        aria-label="Prijsafspraken worden opgehaald"
        class="w-12! shrink-0"
        v-if="isRetrieving"
      />
    </div>

    <div>
      <PFormField v-if="enabled" :label="priceLabel">
        <template v-if="!framed" #hint>
          <span class="flex items-center space-x-1">
            <span v-if="isRetrieving" class="flex items-center space-x-2">
              <span class="text-sm">(wordt opgehaald.. )</span>
              <PProgress
                as="span"
                size="sm"
                aria-label="Prijsafspraken worden opgehaald"
                class="w-12! shrink-0"
              />
            </span>
            <span v-else class="text-sm">({{ entityName }})</span>
          </span>
        </template>
        <PInputNumber
          v-model="unitPriceBinding"
          :placeholder="unitPriceString"
          :disabled="isRetrieving"
          allow-zero
          @input="$emit('input', $event)"
          ref="inputNumber"
        >
          <span class="sm:text-sm">&euro;</span>
        </PInputNumber>
      </PFormField>
      <PCheckbox
        v-model="enabled"
        @change="onCheckboxInput"
        as="label"
        v-else
        class="flex cursor-pointer items-center space-x-2"
      >
        <template #label>
          <span class="text-sm">{{ checkboxLabel }}</span>
        </template>
      </PCheckbox>
    </div>

    <div v-if="showSurchargeModifier">
      <PFormField v-if="surchargeEnabled" :label="surchargeLabel">
        <template v-if="!framed" #hint>
          <span class="flex items-center space-x-1">
            <span v-if="isRetrieving" class="flex items-center space-x-2">
              <span class="text-sm">(wordt opgehaald.. )</span>
              <PProgress
                as="span"
                size="sm"
                aria-label="Prijsafspraken worden opgehaald"
                class="w-12! shrink-0"
              />
            </span>
            <span v-else class="text-sm">({{ entityName }})</span>
          </span>
        </template>
        <PInputNumber
          v-model="surchargeModifierBinding"
          :placeholder="surchargeString"
          :disabled="isRetrieving"
          allow-zero
          @input="$emit('surchargeInput', $event)"
          ref="surchargeInput"
        />
      </PFormField>
      <PCheckbox
        v-model="surchargeEnabled"
        @change="onSurchargeCheckboxInput"
        as="label"
        v-else
        class="flex cursor-pointer items-center space-x-2"
      >
        <template #label>
          <span class="text-sm">{{ surchargeCheckboxLabel }}</span>
        </template>
      </PCheckbox>
    </div>
  </div>
</template>

<script setup lang="ts">
import PFormField from "@/components/form/PFormField.vue"
import PInputNumber from "@/components/form/PInputNumber.vue"
import PProgress from "@/components/elements/PProgress.vue"
import {
  isPriceAgreementTransactionItem,
  type AdministrativeItemPriceAgreement
} from "@/types/models/administrative-events"
import { computed, nextTick, ref, watch } from "vue"
import type { Project } from "@/models/project.model"
import PCheckbox from "@/components/form/PCheckbox.vue"

type PriceAgreementVariant = "project" | "businessEntity"

const modelValue = defineModel<AdministrativeItemPriceAgreement>({ required: true })

const props = withDefaults(
  defineProps<{
    project: Project
    isRetrieving: boolean
    variant: PriceAgreementVariant
    showSurchargeModifier?: boolean
    framed?: boolean
  }>(),
  {
    showSurchargeModifier: false,
    framed: false
  }
)

defineEmits(["input", "surchargeInput"])

const enabled = ref(false)
const surchargeEnabled = ref(false)
function hasUnitPrice(val: AdministrativeItemPriceAgreement | undefined): boolean {
  if (!val) return false
  if (isPriceAgreementTransactionItem(val)) {
    return val.unitPrice != null && val.unitPrice !== ""
  }
  return val.unitPricePerDay != null && val.unitPricePerDay !== ""
}

watch(
  () => modelValue.value,
  (val) => {
    if (val?.isSaved && hasUnitPrice(val)) {
      // Auto-enable if there is an existing custom price.
      enabled.value = true
    }
    if ((val as { surchargeModifier?: string })?.surchargeModifier) {
      // Auto-enable if there is an existing surcharge modifier.
      surchargeEnabled.value = true
    }
  }
)

const inputNumber = ref<HTMLInputElement | null>(null)
function onCheckboxInput() {
  nextTick(() => {
    inputNumber.value!.focus()
  })
}

const surchargeInput = ref<HTMLInputElement | null>(null)
function onSurchargeCheckboxInput() {
  nextTick(() => {
    surchargeInput.value!.focus()
  })
}

const isProjectVariant = computed(() => props.variant === "project")

const entityName = computed(() =>
  isProjectVariant.value ? props.project?.name : props.project.executor.knownBusinessEntity?.name
)

const priceLabel = computed(() => (isProjectVariant.value ? "Projectprijs" : "Bedrijfsprijs"))

const checkboxLabel = computed(() =>
  isProjectVariant.value ? "Aangepaste projectprijs" : "Aangepaste bedrijfsprijs"
)

const unitPriceBinding = computed({
  get: () => {
    if (isPriceAgreementTransactionItem(modelValue.value)) {
      return modelValue.value.unitPrice
    } else {
      return modelValue.value.unitPricePerDay
    }
  },
  set: (value: string) => {
    if (isPriceAgreementTransactionItem(modelValue.value)) {
      modelValue.value.unitPrice = value
    } else {
      modelValue.value.unitPricePerDay = value
    }
  }
})

const unitPriceString = computed(() => {
  const suffix = isProjectVariant.value ? "(project)" : "(bedrijf)"
  if (isPriceAgreementTransactionItem(modelValue.value)) {
    return `Eenheidsprijs ${suffix}`
  } else {
    return `Eenheidsprijs per werkdag ${suffix}`
  }
})

const surchargeLabel = computed(() =>
  isProjectVariant.value ? "Projecttoeslagfactor" : "Bedrijftoeslagfactor"
)

const surchargeCheckboxLabel = computed(() =>
  isProjectVariant.value ? "Aangepaste projecttoeslagfactor" : "Aangepaste bedrijftoeslagfactor"
)

const surchargeString = computed(() => {
  const suffix = isProjectVariant.value ? "(project)" : "(bedrijf)"
  return `toeslagfactor ${suffix}`
})

const surchargeModifierBinding = computed({
  get: () => (modelValue.value as { surchargeModifier?: string }).surchargeModifier ?? "",
  set: (value: string) => {
    ;(modelValue.value as { surchargeModifier?: string }).surchargeModifier = value
  }
})
</script>

<style scoped></style>
